import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiMail, FiLock, FiUser, FiAlertCircle, FiCheck, FiBuilding } = FiIcons;

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Check for guest assessment results that need to be saved
  React.useEffect(() => {
    const savedGuestAssessment = localStorage.getItem('savedGuestAssessment');
    if (savedGuestAssessment) {
      const data = JSON.parse(savedGuestAssessment);
      if (data.type === 'guest_saved') {
        // Show a subtle hint that results will be saved after registration
        console.log('Guest assessment detected - will save after registration');
      }
    }
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const migrateGuestAssessment = (user) => {
    try {
      const savedGuestAssessment = localStorage.getItem('savedGuestAssessment');
      if (savedGuestAssessment) {
        const data = JSON.parse(savedGuestAssessment);
        
        // Create a new assessment record for the authenticated user
        const userAssessment = {
          ...data,
          userId: user.id,
          migratedAt: new Date().toISOString(),
          originalGuestData: true
        };
        
        // Save as the most recent assessment for the user
        localStorage.setItem('assessmentResults', JSON.stringify(userAssessment));
        
        // Clear the guest-specific data
        localStorage.removeItem('savedGuestAssessment');
        localStorage.removeItem('guestAssessment');
        
        console.log('Guest assessment migrated to user account');
        return true;
      }
    } catch (error) {
      console.error('Error migrating guest assessment:', error);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(false);

    // Simulate registration
    setTimeout(() => {
      const users = JSON.parse(localStorage.getItem('users') || '[]');

      // Check if user already exists
      if (users.find((user) => user.email === formData.email)) {
        setError('A user with this email already exists');
        setLoading(false);
        return;
      }

      // Create new user
      const newUser = {
        id: Date.now(),
        name: formData.name,
        email: formData.email,
        password: formData.password, // In production, this should be hashed
        company: formData.company,
        role: 'user',
        createdAt: new Date().toISOString(),
        isActive: true,
      };

      users.push(newUser);
      localStorage.setItem('users', JSON.stringify(users));

      // Check for and migrate guest assessment
      const hasGuestAssessment = migrateGuestAssessment(newUser);

      // Auto-login the user
      const userData = {
        id: newUser.id,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        company: newUser.company
      };
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(userData));

      setSuccess(true);
      setLoading(false);

      // Redirect to dashboard or results after 2 seconds
      setTimeout(() => {
        const redirectPath = hasGuestAssessment ? '/results' : '/dashboard';
        navigate(redirectPath);
      }, 2000);
    }, 1000);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <SafeIcon icon={FiCheck} className="h-16 w-16 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Registration Successful!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Your account has been created. Redirecting to your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-xl overflow-hidden grid lg:grid-cols-2">
        {/* Left side: Form */}
        <div className="p-8 sm:p-12">
          <div className="w-full">
            <div className="flex justify-start items-center space-x-3 mb-6">
              <SafeIcon icon={FiShield} className="h-10 w-10 text-blue-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Create Your Account</h2>
                <p className="text-sm text-gray-600">Join to start improving your team</p>
              </div>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company (Optional)
                </label>
                <div className="mt-1">
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Create a password"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm">
                Already have an account?{' '}
                <a
                  href="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                >
                  Sign in
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Right side: Image and Marketing Text */}
        <div className="hidden lg:flex flex-col justify-center bg-blue-50 p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Unlock Your Team's Potential</h2>
          <p className="text-gray-600 mb-8">
            Register to gain access to a powerful dashboard where you can administer your team's performance, 
            save assessment results, and track their psychological safety journey over time.
          </p>
          <div className="mt-4">
            <img 
              src="https://user-images.githubusercontent.com/1328005/233866804-b399d0e2-89e4-4a7b-a567-c5b6a71306d4.png" 
              alt="Team Dashboard Preview" 
              className="rounded-lg shadow-2xl ring-1 ring-gray-900/10" 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;